import { CLAIM_NOTE_MAX, CLAIM_STATUS_KEYS, CLAIM_STATUS_TEXT, CLAIM_TYPE_TEXT, sum, usd, validateClaimDecision, type ClaimStatusKey, type ClaimTypeKey } from "@flexfreight/core";
import {
  Badge,
  Banner,
  Button,
  DataTable,
  DefinitionList,
  DetailLayout,
  ErrorState,
  FilterToolbar,
  notify,
  PageHeader,
  SearchInput,
  Section,
  Select,
  Skeleton,
  SkeletonText,
  StatusBadge,
  TextAreaField,
  TextField,
  Truncate,
  pageRows,
  sortRows,
  useConfirm,
  useDebouncedText,
  useUrlFilters,
  type Column,
  type FilterItem,
} from "@flexfreight/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api, ApiError } from "../../api/client";
import { carrierName } from "./Shipments";
import { ageDays, BackLink, CustomerLink, day, documentHref, emailOutcomeText, kb, label, message, money, sortOf, sortPatch, time, useCan, when } from "./ops";

export type Claim = {
  id: string;
  orgId: string;
  orgName: string;
  shipmentId: string;
  type: ClaimTypeKey;
  status: ClaimStatusKey;
  description: string;
  amountCents: number;
  carrierClaimNumber: string | null;
  documentIds: string[];
  resolutionNote: string | null;
  paidAmountCents: number | null;
  createdAt: string;
  updatedAt: string;
  nextStatuses: ClaimStatusKey[];
};
type ClaimDetail = {
  claim: Claim;
  shipment: { id: string; carrier: string; status: string; bolNumber: string | null; proNumber: string | null; pickupDate: string | null; origin: string; destination: string };
  documents: { id: string; kind: string; contentType: string; sizeBytes: number; createdAt: string; attached: boolean }[];
  history: { action: string; at: string; from: string | null; to: string | null }[];
};

const ACTION_TEXT: Partial<Record<ClaimStatusKey, string>> = { under_review: "Start review", approved: "Approve", denied: "Deny", paid: "Mark paid", withdrawn: "Withdraw" };
const historyText = (h: ClaimDetail["history"][number]) =>
  h.action === "claim.create" ? `Filed${h.to ? ` (${label(h.to)})` : ""}` : h.action === "claim.status" ? `${h.from ? label(h.from) : "?"} → ${h.to ? label(h.to) : "?"}` : h.action === "claim.update" ? "Details updated" : label(h.action.replace(/^claim\./, ""));
const FILTERS = { status: "", type: "", q: "", sort: "", dir: "asc", page: 0 };
const PAGE_SIZE = 50;

/** Freight > Claims: the claims queue across customers, oldest first, with the review on each claim's page. */
export function ClaimsPage() {
  const f = useUrlFilters(FILTERS, useSearchParams());
  const v = f.values;
  const navigate = useNavigate();
  const status = (CLAIM_STATUS_KEYS as readonly string[]).includes(v.status) ? (v.status as ClaimStatusKey) : null;
  const [search, setSearch] = useDebouncedText(v.q, (q) => f.set({ q }));
  const list = useQuery({
    queryKey: ["console", "claims", status ?? "queue"],
    queryFn: () => api<Claim[]>(status ? `/v1/admin/claims?${new URLSearchParams({ status })}` : "/v1/admin/claims"),
    placeholderData: keepPreviousData,
  });
  const needle = v.q.toLowerCase();
  const sort = sortOf(v);
  const all = sortRows(
    (list.data ?? []).filter((c) => (!v.type || c.type === v.type) && (!needle || [c.orgName, c.description, c.carrierClaimNumber].some((x) => x?.toLowerCase().includes(needle)))),
    sort,
    (c, k) => (k === "filed" ? time(c.createdAt) : k === "customer" ? c.orgName : k === "claim" ? CLAIM_TYPE_TEXT[c.type] : k === "amount" ? c.amountCents : k === "status" ? CLAIM_STATUS_TEXT[c.status] : null),
  );
  const rows = pageRows(all, v.page, PAGE_SIZE);
  const totalCents = sum(all.map((c) => usd(c.amountCents))).cents;
  const href = (c: Claim) => `/claims/${c.orgId}/${c.id}`;
  const items: FilterItem[] = [
    {
      key: "status",
      priority: 1,
      active: status !== null,
      inline: <Select variant="toolbar" label="Status" value={status ?? ""} onChange={(s) => f.set({ status: s })} options={[{ value: "", label: "Waiting on us" }, ...CLAIM_STATUS_KEYS.map((s) => ({ value: s, label: CLAIM_STATUS_TEXT[s] }))]} />,
    },
    {
      key: "type",
      priority: 2,
      active: v.type !== "",
      inline: <Select variant="toolbar" label="Type" value={v.type} onChange={(type) => f.set({ type })} options={[{ value: "", label: "Any" }, ...(Object.keys(CLAIM_TYPE_TEXT) as ClaimTypeKey[]).map((t) => ({ value: t, label: CLAIM_TYPE_TEXT[t] }))]} />,
    },
  ];
  const columns: Column<Claim>[] = [
    { key: "filed", header: "Filed", sortable: true, width: "8.5rem", render: (c) => <>{day(c.createdAt)}<span className="ffa-block ffa-muted">{ageDays(c.createdAt)} days ago</span></> },
    { key: "customer", header: "Customer", sortable: true, truncate: "custom", render: (c) => <Truncate text={c.orgName}><CustomerLink orgId={c.orgId} name={c.orgName} /></Truncate> },
    {
      key: "claim",
      header: "Claim",
      primary: true,
      sortable: true,
      truncate: "custom",
      render: (c) => (
        <span className="ffx-cellstack">
          <Link className="ffa-rowlink ffa-cell-strong" to={href(c)}>{CLAIM_TYPE_TEXT[c.type]}</Link>
          <Truncate text={c.description}><span className="ffa-muted">{c.description}</span></Truncate>
        </span>
      ),
    },
    { key: "amount", header: "Claimed", numeric: true, sortable: true, width: "8rem", render: (c) => money(c.amountCents) },
    { key: "status", header: "Status", sortable: true, width: "8.5rem", render: (c) => <StatusBadge domain="claim" value={c.status} /> },
  ];
  return (
    <section className="ffa-page">
      <PageHeader title="Claims" subtitle={`${status ? CLAIM_STATUS_TEXT[status] : "Claims waiting on FlexFreight (filed, under review, approved)"}, oldest first.`} />
      <FilterToolbar
        label="Claim filters"
        search={<SearchInput label="Search customer, description or carrier claim #" value={search} onChange={setSearch} />}
        items={items}
        anyActive={f.anyActive}
        showReset
        onReset={() => {
          setSearch("");
          f.reset();
        }}
        meta={list.data ? `${all.length} claim${all.length === 1 ? "" : "s"} · ${money(totalCents)}` : undefined}
      />
      <DataTable
        caption="Claims"
        columns={columns}
        rows={rows}
        rowKey={(c) => c.id}
        loading={list.isPending || list.isFetching}
        error={list.isError ? { title: "Couldn't load claims", message: message(list.error), onRetry: () => void list.refetch() } : null}
        empty={f.anyActive ? "No claims match these filters." : "No claims waiting on us."}
        sort={sort}
        onSortChange={(s) => f.set(sortPatch(s))}
        onRowClick={(c) => navigate(href(c))}
        rowHref={href}
        pagination={all.length > PAGE_SIZE ? { page: v.page, pageSize: PAGE_SIZE, total: all.length, onPageChange: (page) => f.set({ page }) } : undefined}
      />
    </section>
  );
}

type ClaimDoc = ClaimDetail["documents"][number];

/** Freight > Claims > one claim: DetailLayout with the report, shipment and documents, and the decision beside them. */
export function ClaimPage() {
  const { orgId = "", claimId = "" } = useParams();
  const can = useCan();
  const key = ["console", "claim", orgId, claimId];
  const q = useQuery({ queryKey: key, queryFn: () => api<ClaimDetail>(`/v1/admin/orgs/${orgId}/claims/${claimId}`) });
  const back = <BackLink to="/claims">Claims</BackLink>;
  if (q.isPending) {
    return (
      <section className="ffa-page" aria-busy="true">
        <DetailLayout header={<PageHeader back={back} eyebrow="Claim" title={<Skeleton width="16rem" height="2.25rem" />} subtitle={<Skeleton width="20rem" />} />} aside={<Section title="Decision"><SkeletonText lines={4} /></Section>}>
          <Section><SkeletonText lines={6} /></Section>
        </DetailLayout>
        <span className="ffx-sr">Loading claim…</span>
      </section>
    );
  }
  if (q.isError) {
    const missing = q.error instanceof ApiError && q.error.status === 404;
    return (
      <section className="ffa-page">
        <PageHeader back={back} title={missing ? "Claim not found" : "Claim"} />
        {missing ? <Banner tone="warning" title="This claim doesn't exist.">It may have been withdrawn, or the link is wrong.</Banner> : <ErrorState title="Couldn't load this claim" message={message(q.error)} onRetry={() => void q.refetch()} />}
      </section>
    );
  }
  const { claim: c, shipment: s, documents, history } = q.data;
  const docCols: Column<ClaimDoc>[] = [
    {
      key: "kind",
      header: "Document",
      primary: true,
      render: (d) => (
        <span className="ffx-cellrow">
          <a href={documentHref(s.id, d.id)} target="_blank" rel="noopener noreferrer">{label(d.kind)}</a>
          {d.attached ? <Badge tone="info" size="sm" dot={false}>On the claim</Badge> : null}
        </span>
      ),
    },
    { key: "added", header: "Added", width: "9rem", render: (d) => day(d.createdAt) },
    { key: "size", header: "Size", numeric: true, width: "6rem", render: (d) => kb(d.sizeBytes) },
  ];
  const decide = can("claims:write") && c.nextStatuses.length > 0;
  const number = can("claims:write") && ["draft", "filed", "under_review"].includes(c.status);
  return (
    <section className="ffa-page">
      <DetailLayout
        header={
          <PageHeader
            back={back}
            eyebrow="Claim"
            title={`${CLAIM_TYPE_TEXT[c.type]} claim · ${money(c.amountCents)}`}
            badges={<StatusBadge domain="claim" value={c.status} />}
            subtitle={<><CustomerLink orgId={c.orgId} name={c.orgName} /> · filed {day(c.createdAt)} · {s.bolNumber ?? "No BOL"} with {carrierName(s.carrier)}</>}
            primaryAction={<Link className="ffx-btn" to={`/shipments/${s.id}`}>Open shipment</Link>}
          />
        }
        aside={
          <>
            {decide ? <Decision claim={c} queryKey={key} /> : null}
            {number ? <CarrierNumber claim={c} queryKey={key} /> : null}
            <Section title="History">
              {history.length === 0 ? (
                <p className="ffa-muted">No recorded changes yet.</p>
              ) : (
                <ol className="ffa-timeline">
                  {history.map((h, i) => (
                    <li key={`${h.at}-${i}`}>
                      <time dateTime={h.at} className="ffa-muted">{day(h.at)}</time>
                      <span>{historyText(h)}</span>
                    </li>
                  ))}
                </ol>
              )}
            </Section>
          </>
        }
        asideLabel="Claim review"
      >
        <Section title="What the customer reported">
          <p className="ffa-msg__body">{c.description}</p>
          <DefinitionList
            items={[
              { term: "Claimed", detail: <span className="ffx-num">{money(c.amountCents)}</span> },
              c.paidAmountCents !== null ? { term: "Paid", detail: <span className="ffx-num">{money(c.paidAmountCents)}</span> } : null,
              { term: "Carrier claim #", detail: c.carrierClaimNumber ?? "Not filed with the carrier yet" },
              c.resolutionNote ? { term: "Note to customer", detail: c.resolutionNote } : null,
              { term: "Last update", detail: when(c.updatedAt) },
            ]}
          />
        </Section>
        <Section title="Shipment">
          <DefinitionList
            items={[
              { term: "BOL", detail: <Link to={`/shipments/${s.id}`}>{s.bolNumber ?? "No BOL"}</Link> },
              { term: "PRO", detail: s.proNumber ?? "-" },
              { term: "Carrier", detail: carrierName(s.carrier) },
              { term: "Lane", detail: `${s.origin} → ${s.destination}` },
              { term: "Pickup", detail: day(s.pickupDate) },
              { term: "Status", detail: <StatusBadge domain="shipment" value={s.status} /> },
            ]}
          />
        </Section>
        <Section title="Documents" flush>
          <DataTable caption="Documents" columns={docCols} rows={documents} rowKey={(d) => d.id} empty="No documents on this shipment." maxHeight="none" />
        </Section>
      </DetailLayout>
    </section>
  );
}

function Decision({ claim, queryKey }: { claim: Claim; queryKey: string[] }) {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [to, setTo] = useState<ClaimStatusKey>(claim.nextStatuses[0]!);
  const [note, setNote] = useState("");
  const [paid, setPaid] = useState("");
  const [err, setErr] = useState<{ field: "note" | "paid"; message: string } | null>(null);
  const save = useMutation({
    mutationFn: (body: object) => api<Claim & { customerEmail: "queued" | "not_connected" | "not_sent" }>(`/v1/admin/orgs/${claim.orgId}/claims/${claim.id}/status`, { method: "POST", body }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey });
      void qc.invalidateQueries({ queryKey: ["console", "claims"] });
      notify.success(`Claim ${CLAIM_STATUS_TEXT[r.status].toLowerCase()}`, emailOutcomeText(r.customerEmail));
      setNote("");
      setPaid("");
    },
    onError: (e) => notify.error("Not saved", message(e)),
  });
  const submit = async () => {
    const r = validateClaimDecision(to, note, paid);
    if (!r.ok) return setErr({ field: r.field, message: r.message });
    setErr(null);
    if (to === "paid" && !(await confirm({ title: `Mark paid at ${money(r.extra.paidAmountCents!)}?`, body: `The customer claimed ${money(claim.amountCents)}. A paid claim is closed and can't be changed.`, confirmLabel: "Mark paid" }))) return;
    if (to === "denied" && !(await confirm({ title: "Deny this claim?", body: "The customer sees your note.", confirmLabel: "Deny claim", danger: true }))) return;
    save.mutate({ status: to, ...r.extra });
  };
  return (
    <Section title="Decision" description="The customer is told the outcome and sees your note.">
      <Select label="Next step" value={to} onChange={(x) => setTo(x as ClaimStatusKey)} options={claim.nextStatuses.map((s) => ({ value: s, label: ACTION_TEXT[s] ?? CLAIM_STATUS_TEXT[s] }))} />
      {to === "paid" ? <TextField label="Amount paid" prefix="$" inputMode="decimal" value={paid} onChange={(e) => setPaid(e.target.value)} error={err?.field === "paid" ? err.message : undefined} hint={`Up to the ${money(claim.amountCents)} claimed.`} /> : null}
      <TextAreaField label="Note to the customer" value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={CLAIM_NOTE_MAX} optional={to !== "denied"} error={err?.field === "note" ? err.message : undefined} />
      <div className="ffx-actions">
        <Button variant={to === "denied" ? "destructive" : "primary"} loading={save.isPending} onClick={() => void submit()}>{ACTION_TEXT[to] ?? "Save"}</Button>
      </div>
      {dialog}
    </Section>
  );
}

function CarrierNumber({ claim, queryKey }: { claim: Claim; queryKey: string[] }) {
  const qc = useQueryClient();
  const [v, setV] = useState(claim.carrierClaimNumber ?? "");
  const save = useMutation({
    mutationFn: () => api(`/v1/admin/orgs/${claim.orgId}/claims/${claim.id}`, { method: "PATCH", body: { carrierClaimNumber: v.trim() || null } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey });
      notify.success("Carrier claim number saved");
    },
    onError: (e) => notify.error("Not saved", message(e)),
  });
  return (
    <Section title="Carrier claim">
      <TextField label="Carrier claim #" value={v} onChange={(e) => setV(e.target.value)} maxLength={60} optional />
      <div className="ffx-actions">
        <Button size="sm" loading={save.isPending} disabled={v.trim() === (claim.carrierClaimNumber ?? "")} onClick={() => save.mutate()}>Save</Button>
      </div>
    </Section>
  );
}
